import { ModelMapResponse } from '@/store/models'
import { getState } from '@/utils/sse'
import {
  CompletionApiRequest,
  GeneratePromptRequest,
  isRefusalFinishReason,
  ModelMessage,
  ProviderInventoryList,
  GenerateResult,
  StreamingCompletionApiRequest,
  StreamingAgentCompletionApiRequest,
  TokenUsage,
  GenerateResultProviderState
} from '@/types/msq'
import {
  NewModelRequest as ApiNewModelRequest,
  DiscoverModelsRequest,
  DeleteModelRequest,
  ModelsResponse
} from '@/types/models'
import {
  AgentConfigs,
  DeleteAgentRequest,
  NewAgentRequest,
  AgentConfig,
  AgentToolOptions,
  AgentEmbeddingOptions,
  AgentVoiceConfig
} from '@/types/agents'
import {
  GetPackageInfoResponse,
  GetPackagesResponse,
  GetPackageUpdatesResponse,
  InstallPackageRequest,
  InstallPackageResponse,
  MCPServer,
  UserVisibleMcpServer,
  Tool,
  ToolMethod,
  UpgradeAllPackagesResponse,
  UpgradePackageResponse,
  McpToolCallRequest,
  McpToolCallResponse,
  CreateExternalMcpServerRequest,
  DiscoverExternalMcpOAuthRequest,
  DiscoverExternalMcpOAuthResponse,
  InstallExternalMcpServerRequest,
  UpdateExternalMcpServerInstallRequest,
  SaveExternalMcpServerSecretsRequest,
  StartExternalMcpOAuthRequest,
  StartExternalMcpOAuthResponse,
  CatalogExternalMcpServer,
  RequestMcpServerInstallPayload,
  RequestMcpServerInstallResponse
} from '@/types/mcp'
import { ProviderConfig, ProviderKey, DeleteProviderRequest } from '@/types/providers'
import { SpeechTranscriptionResponse } from '@/types/speechProcessing'
import {
  VectorStore,
  DeleteResponse,
  VectorStoreFilesResponse,
  VectorStoreFileResponse,
  StaticChunkStrategy,
  VectorStoreFileRequest,
  VectorStoreFile,
  FileUpload,
  ListUserFilesResponse,
  GetFileByIdResponse,
  VectorStoreListResponse,
  VectorStoreWithFiles,
  VectorStoreResponse,
  EmbeddingSearchResult,
  EmbeddingCollection, // Added for local collections
  UserVectorFile
} from '@/types/embeddings'
import axios from 'axios'
import {
  isStatusEvent,
  isTextDeltaEvent,
  isMessageStopEvent,
  isAudioChunkEvent,
  isAudioStopEvent,
  isErrorEvent,
  isRunEvent,
  isSnapshotEvent,
  isUsageEvent,
  type StatusEvent,
  type SnapshotEvent
} from '@/types/speak'
import { PublicAgentDetails, PublicChatSession, AgentShareDTO } from '@/types/public'
import {
  ApiErrorResponse,
  isApiError,
  AuthCheckResponse,
  RequestParams,
  SignInResponse,
  SignUpRequest,
  SignUpResponse,
  StripePortalSessionResponse,
  StripeSubscriptionResponse,
  GetApiKeysResponse,
  GenerateApiKeyResponse,
  DeleteApiKeyResponse, // Added import
  RequestPasswordResetResponse,
  ResetPasswordResponse,
  SendResultsEmailRequest,
  SendResultsEmailResponse,
  AdminListUsersResponse,
  AdminTogglePublishResponse,
  AdminProvisionRequest,
  AdminProvisionResponse,
  AdminProvisioningStateResponse,
  // Signup Codes types
  SignupCodesStatusResponse,
  CreateSignupCodesRequest,
  CreateSignupCodesResponse,
  ListSignupCodesResponse,
  ToggleSignupCodeResponse,
  DeleteSignupCodeResponse,
  InviteSignupCodeRequest,
  InviteSignupCodeResponse,
  // Usage metrics types
  UsageSummaryResponse,
  UsageByModelResponse,
  UsageTimeseriesResponse,
  BillingResponse,
  // User settings DTOs
  GetUserSettingsResponseDTO,
  UpdateUserSettingsRequestDTO,
  UpdateUserSettingsResponseDTO,
  // Admin MCP Server Request types
  AdminSharedServerDeleteImpactResponse,
  AdminListMcpServerRequestsResponse,
  AdminUpdateMcpServerRequestPayload,
  AdminUpdateMcpServerRequestResponse
} from '@/types/api'
import { ScheduledRun, ScheduledRunResult, NewScheduledRunPayload, UpdateScheduledRunPayload } from '@/types/schedules';
import type { VoiceConfig, AddVoiceRequest, AddVoiceResponse } from '@/types/voices'
import type { WorkflowConfig, WorkflowRunHydratedRecord, WorkflowRunRecord } from '@/types/workflows'
import { ModelPricingQueryResponse } from '@/types/modelPricing'
import type {
  FactoryConfig,
  FactoryRunRecord,
  FactoryScheduleRecord,
  FactoryStepRunRecord,
  HydratedFactoryStepRunRecord,
  NewFactorySchedulePayload
} from '@/types/factories'
import type {
  VideoProcessingRunRecord,
  VideoProcessingStreamFrame,
  VideoToWorkflowUploadRequest
} from '@/types/videoProcessing'
import type {
  PageCategoriesResponse,
  PageDraftInput,
  PageDeleteResponse,
  PageLayout,
  PageLayoutSchemaResponse,
  PageListResponse,
  PagePreviewRunResponse,
  PagePreviewTokenResponse,
  PageResponse,
  PageRunsResponse,
  X402NetworksResponse,
  X402SettlementsResponse
} from '@/types/pages'

export interface FactoryRunActionResponse {
  success: boolean
  data: FactoryRunRecord
  noop?: boolean
  message?: string
}

const defaultContentType = 'application/json'

// Local helper to build query strings consistently
function toQuery(params: Record<string, string | number | boolean | undefined>): string {
  const q = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
  return q.length ? `?${q.join('&')}` : ''
}

type ApiConversationMessage = Omit<ModelMessage, 'tokenUsage' | 'toolEvents' | 'retryState' | 'finishReason'>

function stripUiOnlyMessageMetadata(message: ModelMessage): ApiConversationMessage {
  const { tokenUsage, toolEvents, retryState, finishReason, ...rest } = message
  return { ...rest }
}

function hasReplayableAnthropicAssistantState(message: Pick<ModelMessage, 'providerState' | 'rawContentBlocks'>): boolean {
  if (message.providerState?.anthropic?.assistantTurnBoundary === true) {
    return true
  }

  const providerStateRawBlocks = message.providerState?.anthropic?.rawContentBlocks
  if (Array.isArray(providerStateRawBlocks) && providerStateRawBlocks.length > 0) {
    return true
  }

  return Array.isArray(message.rawContentBlocks) && message.rawContentBlocks.length > 0
}

function hasSubstantiveMessageContent(content: ModelMessage['content']): boolean {
  if (typeof content === 'string') {
    return content.trim().length > 0
  }

  return Array.isArray(content) && content.length > 0
}

function shouldRetainConversationMessageForApi(message: ApiConversationMessage): boolean {
  if (message.role === 'tool') {
    return true
  }

  if (message.role === 'assistant') {
    if (Array.isArray(message.tool_calls) && message.tool_calls.length > 0) {
      return true
    }
    if (hasReplayableAnthropicAssistantState(message)) {
      return true
    }
  }

  return hasSubstantiveMessageContent(message.content)
}

function filterConversationMessages(messages: ModelMessage[]): ModelMessage[] {
  return messages
    .map((message) => ({ ...message }))
    .filter((message) => shouldRetainConversationMessageForApi(stripUiOnlyMessageMetadata(message)))
}

function sanitizeConversationMessagesForApi(messages: ModelMessage[]): ApiConversationMessage[] {
  return filterConversationMessages(messages).map(stripUiOnlyMessageMetadata)
}

function sanitizeProviderStateForApi(
  providerState: GenerateResultProviderState | undefined
): GenerateResultProviderState | undefined {
  if (!providerState?.anthropic) {
    return providerState
  }

  const anthropicState: NonNullable<GenerateResultProviderState['anthropic']> = {}
  if (Array.isArray(providerState.anthropic.rawContentBlocks)) {
    anthropicState.rawContentBlocks = providerState.anthropic.rawContentBlocks
  }
  if (providerState.anthropic.assistantTurnBoundary === true) {
    anthropicState.assistantTurnBoundary = true
  }

  return Object.keys(anthropicState).length > 0 ? { anthropic: anthropicState } : undefined
}

export async function requestUrl<T>({
  method,
  url,
  headers,
  data,
  apiKey,
  file,
  contentType,
  sessionId,
  clientId,
  timeout,
  responseType
}: RequestParams): Promise<T | ApiErrorResponse> {
  headers = headers ?? {}
  if (!file) {
    headers['Content-Type'] = contentType ?? defaultContentType
  }
  if (apiKey) {
    headers['Authorization'] = `Bearer ${apiKey}`
  }
  if (sessionId) {
    headers['X-Session-ID'] = sessionId
  }
  if (clientId) {
    headers['X-Client-ID'] = clientId
  }

  try {
    const config: any = {
      method,
      url,
      headers,
      data,
      responseType: responseType ?? 'json'
    }
    if (file) {
      config.form = data
    }
    if (timeout) {
      config.timeout = timeout
    }

    const response = await axios(config)
    return response.data as T
  } catch (error: any) {
    const msg = `Request failed. URL: [${method}] ${url}\nError: `
    console.error(msg, error)

    if (error?.response?.status === 401 && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth-expired', { detail: { url, status: 401 } }))
    }

    // Extract error message from the API response if available
    let errorMessage = 'Unknown error occurred'
    if (typeof error.response?.data?.error?.message === 'string' && error.response.data.error.message.length > 0) {
      errorMessage = error.response.data.error.message
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message
    } else if (typeof error.response?.data?.error === 'string') {
      errorMessage = error.response.data.error
    } else if (error.message) {
      errorMessage = error.message
    }

    // Create enhanced error response with detailed information
    const errorResponse: ApiErrorResponse = {
      error: true,
      message: errorMessage
    }

    // Check if it's an Axios error and extract additional details
    if (error.isAxiosError) {
      // Add status information
      if (error.response) {
        errorResponse.statusCode = error.response.status
        errorResponse.statusText = error.response.statusText
        errorResponse.data = error.response.data
        errorResponse.headers = error.response.headers
      }

      // Add request configuration (excluding sensitive data)
      if (error.config) {
        // Create a sanitized copy of the config to avoid exposing sensitive data
        const sanitizedConfig = { ...error.config }
        // Remove potentially sensitive headers like Authorization
        if (sanitizedConfig.headers?.Authorization) {
          sanitizedConfig.headers = { ...sanitizedConfig.headers }
          sanitizedConfig.headers.Authorization = '[REDACTED]'
        }
        errorResponse.config = sanitizedConfig
      }
    }

    return errorResponse
  }
}

// Contact form submission
export async function submitContactForm(contactData: {
  name: string
  email: string
  subject: string
  message: string
}) {
  return requestUrl<{ success: boolean; message: string }>({
    method: 'POST',
    url: '/api/public/contact',
    data: contactData
  })
}

export async function sendResultsEmail(data: SendResultsEmailRequest): Promise<SendResultsEmailResponse | ApiErrorResponse> {
  return requestUrl<SendResultsEmailResponse>({
    method: 'POST',
    url: '/api/send-results-email',
    data
  })
}

export async function submitEmail(email: string) {
  const response = await requestUrl({
    method: 'POST',
    url: '/email',
    data: { email }
  })
  return response
}

export async function signUp({ username, first, last, password, email, signupCode }: SignUpRequest): Promise<SignUpResponse> {
  const failedSignup = { success: false, message: 'Could not sign up. Check invite code.' }
  const payload: any = { username, first, last, password, email }
  if (signupCode) payload.signupCode = signupCode
  const response = await requestUrl<SignUpResponse>({
    method: 'POST',
    url: '/api/register',
    data: payload
  })

  // Handle API error response
  if (response && 'error' in response) {
    return { success: false, message: response.message }
  }
  
  return response ?? failedSignup
}

// --- Signup Codes (Public) ---

export async function getSignupCodesStatus() {
  return requestUrl<SignupCodesStatusResponse>({
    method: 'GET',
    url: '/api/signup-codes/status'
  })
}

// --- Signup Codes (Admin) ---

export async function adminCreateSignupCodes(data: CreateSignupCodesRequest) {
  return requestUrl<CreateSignupCodesResponse>({
    method: 'POST',
    url: '/v1/core/admin/signup-codes',
    data
  })
}

export async function adminListSignupCodes(params?: { active?: boolean; email?: string; limit?: number; offset?: number }) {
  const q: string[] = []
  if (params?.active !== undefined) q.push(`active=${params.active}`)
  if (params?.email) q.push(`email=${encodeURIComponent(params.email)}`)
  if (params?.limit !== undefined) q.push(`limit=${params.limit}`)
  if (params?.offset !== undefined) q.push(`offset=${params.offset}`)
  const qs = q.length ? `?${q.join('&')}` : ''
  return requestUrl<ListSignupCodesResponse>({
    method: 'GET',
    url: `/v1/core/admin/signup-codes${qs}`
  })
}

export async function adminActivateSignupCode(id: string) {
  return requestUrl<ToggleSignupCodeResponse>({
    method: 'POST',
    url: `/v1/core/admin/signup-codes/${encodeURIComponent(id)}/activate`
  })
}

export async function adminDeactivateSignupCode(id: string) {
  return requestUrl<ToggleSignupCodeResponse>({
    method: 'POST',
    url: `/v1/core/admin/signup-codes/${encodeURIComponent(id)}/deactivate`
  })
}

export async function adminDeleteSignupCode(id: string) {
  return requestUrl<DeleteSignupCodeResponse>({
    method: 'DELETE',
    url: `/v1/core/admin/signup-codes/${encodeURIComponent(id)}`
  })
}

export async function adminInviteSignupCode(data: InviteSignupCodeRequest) {
  return requestUrl<InviteSignupCodeResponse>({
    method: 'POST',
    url: '/v1/core/admin/signup-codes/invite',
    data
  })
}

// get models
// Accepts Partial<ProviderConfig> to handle both configured (only providerKey needed)
// and unconfigured (apiKey/url might be needed) scenarios.
export async function modelsRequest(
  providerDetails: DiscoverModelsRequest
): Promise<ProviderInventoryList | ApiErrorResponse> {
  // The backend /v1/core/models endpoint now handles logic based on whether
  // the providerKey is already configured. We just pass the details.
  return requestUrl<ProviderInventoryList>({
    method: 'POST',
    url: '/v1/core/models',
    data: providerDetails // Send providerKey and potentially url/apiKey if provided
  })
}

export async function queryModelPricing(data: {
  models: Array<{ providerKey: string; model: string }>
}): Promise<ModelPricingQueryResponse | ApiErrorResponse> {
  return requestUrl<ModelPricingQueryResponse>({
    method: 'POST',
    url: '/v1/core/pricing/query',
    data
  })
}

// --- API Key Endpoints ---

export async function getApiKeys() {
  return requestUrl<GetApiKeysResponse>({
    method: 'GET',
    url: '/api/users/apikeys'
  })
}

export async function generateApiKey() {
  return requestUrl<GenerateApiKeyResponse>({
    method: 'POST',
    url: '/api/users/apikeys'
    // No request body needed
  })
}

export async function deleteApiKey(id: string) {
  return requestUrl<DeleteApiKeyResponse>({
    method: 'DELETE',
    url: `/api/users/apikeys/${id}`
  })
}

// Password Reset Functions
export async function requestPasswordReset(email: string): Promise<RequestPasswordResetResponse | ApiErrorResponse> {
  return requestUrl<RequestPasswordResetResponse>({
    method: 'POST',
    url: '/api/reqresetpassword',
    data: { email }
  })
}

export async function resetPassword(token: string, password: string): Promise<ResetPasswordResponse | ApiErrorResponse> {
  return requestUrl<ResetPasswordResponse>({
    method: 'POST',
    url: '/api/resetpassword',
    data: { token, password }
  })
}

export async function modelMapRequest() {
  return requestUrl<ModelMapResponse>({
    method: 'GET',
    url: '/v1/modelmap'
  })
}

// Use the imported, updated NewModelRequest type
export type NewModelRequest = ApiNewModelRequest

// add model
// Accepts the updated NewModelRequest type (no url/apiKey)
export async function addModel(modelConfig: NewModelRequest) {
  const response = await requestUrl<{ status: string; message: string }>({
    method: 'POST',
    url: '/v1/core/add/model',
    // Send the data as received, backend expects providerKey, model, name, etc.
    data: modelConfig
  })
  return response
}

// delete model
export async function deleteModel(modelId: string) {
  const requestData: DeleteModelRequest = { modelId }
  const response = await requestUrl<{ status: string; message: string }>({
    method: 'POST',
    url: '/v1/core/delete/model',
    data: requestData
  })
  return response
}

// completion requests

export async function generatePromptRequest({ prompt, modelConfig }: GeneratePromptRequest) {
  // Backend expects model name, not the full config object anymore for this specific utility endpoint
  const { name: modelName, options: modelOptions } = modelConfig
  const response = await requestUrl<{ data: string }>({
    method: 'POST',
    url: '/v1/core/generate/prompt',
    data: {
      model: modelName,
      messages: [{ role: 'user', content: prompt }],
      name: modelName,
      description: '',
      type: 'agent',
      modelOptions
    }
  })
  return response
}

// chat completion
// No longer needs to handle apiKey/url
export async function completionRequest({
  messages,
  modelConfig,
  options,
  onRetry,
  timeout,
  providerState,
  requestTimeoutMs,
  maxRetries
}: CompletionApiRequest) {
  // Get the current session ID and client ID from the sse utility
  const { sessionId, clientId } = getState()

  // Strip UI-only metadata before sending
  const messagesForApi = sanitizeConversationMessagesForApi(messages)
  const sanitizedProviderState = sanitizeProviderStateForApi(providerState)

  // Backend expects model name (user-configured name)
  const modelName = modelConfig.name
  const response = await requestUrl<GenerateResult>({
    method: 'POST',
    url: '/v1/chat/completions',
    sessionId, // Use the session ID from the utility
    clientId, // Use the client ID from the utility
    data: {
      model: modelName, // Send user-configured model name
      messages: messagesForApi, // Use stripped messages
      // Merge model's base options with request-specific options
      options: { ...modelConfig.options, ...options },
      ...(sanitizedProviderState ? { providerState: sanitizedProviderState } : {}),
      ...(requestTimeoutMs !== undefined ? { requestTimeoutMs } : {}),
      ...(maxRetries !== undefined ? { maxRetries } : {})
    },
    onRetry,
    timeout
  })
  return response
}

// agent completion
// No longer needs to handle apiKey/url
export async function agentCompletionRequest({
  messages,
  name,
  providerState,
  timeout,
  requestTimeoutMs,
  maxRetries
}: {
  messages: ModelMessage[]
  name: string
  providerState?: GenerateResultProviderState
  timeout?: number
  requestTimeoutMs?: number
  maxRetries?: number
}) {
  // Get the current session ID and client ID from the sse utility
  const { sessionId, clientId } = getState()

  // Strip UI-only metadata before sending
  const messagesForApi = sanitizeConversationMessagesForApi(messages)
  const sanitizedProviderState = sanitizeProviderStateForApi(providerState)

  const response = await requestUrl<GenerateResult>({
    method: 'POST',
    url: '/v1/chat/completions', // Still uses the same endpoint
    sessionId, // Use the session ID from the utility
    clientId, // Use the client ID from the utility
    data: {
      model: name, // Send agent name as the 'model' identifier
      messages: messagesForApi,
      ...(sanitizedProviderState ? { providerState: sanitizedProviderState } : {}),
      ...(requestTimeoutMs !== undefined ? { requestTimeoutMs } : {}),
      ...(maxRetries !== undefined ? { maxRetries } : {})
      // Agent-specific options are handled by the backend
    },
    timeout
  })
  return response
}

// --- Streaming Requests ---

export interface StreamingRequestHandle {
  controller: AbortController
  promise: Promise<void>
  readonly runId?: string
}

function isAbortError(error: unknown): boolean {
  if (typeof DOMException !== 'undefined' && error instanceof DOMException) {
    return error.name === 'AbortError'
  }
  if (error instanceof Error) {
    return error.name === 'AbortError'
  }
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error as { name?: string }).name === 'AbortError'
  )
}

function createStreamingController(signal?: AbortSignal): AbortController {
  const controller = new AbortController()
  if (!signal) return controller

  if (signal.aborted) {
    controller.abort()
    return controller
  }

  signal.addEventListener('abort', () => controller.abort(), { once: true })
  return controller
}

async function handleStreamingRequest(
  url: string,
  body: object,
  callbacks: {
    onChunk: (chunk: string) => void
    onComplete: (finalContent: string, usage?: TokenUsage, finishReason?: string | null) => void
    onError: (error: Error) => void
    onCancel?: () => void
    onMeta?: (meta: { runId?: string; contextMessages?: any[]; providerState?: GenerateResultProviderState }) => void
    onReset?: () => void
  },
  timeout?: number,
  signal?: AbortSignal,
  sessionIdOverride?: string
) {
  const { sessionId: stateSessionId, clientId } = getState()
  const sessionId = sessionIdOverride ?? stateSessionId
  let finalContent = ''
  let usage: TokenUsage | undefined
  let finishReason: string | null = null

  // Single handler for every parsed SSE data frame, shared by the main loop and the tail/leftover
  // parsers so all frame types (context, reset, content deltas, usage) are treated identically.
  const processParsedFrame = (parsed: any): void => {
    if (parsed.object === 'chat.completion.context' && parsed.messages) {
      if (callbacks.onMeta) {
        callbacks.onMeta({ contextMessages: parsed.messages, providerState: parsed.providerState })
      }
    } else if (parsed.object === 'chat.completion.reset') {
      // Server is retrying this assistant turn (e.g. Anthropic PTC empty-boundary recovery).
      // Discard everything streamed for it so far; the retry streams a fresh response in its place.
      finalContent = ''
      finishReason = null
      if (callbacks.onReset) {
        callbacks.onReset()
      }
    } else if (parsed.choices && parsed.choices[0]?.delta?.content) {
      const contentChunk = parsed.choices[0].delta.content
      finalContent += contentChunk
      callbacks.onChunk(contentChunk)
    }
    // Capture usage if provided in a chunk before [DONE]
    if (parsed.usage) {
      usage = parsed.usage
    }
    // Capture the turn's terminal finish reason. One arrives per model turn (a tool-calling turn ends
    // with 'tool_calls', the answering turn with 'stop'), so the latest non-null value normally wins.
    // A refusal is the exception: it is terminal at the provider, and the api's trailing usage chunk
    // carries finish_reason 'stop' on some surfaces, which would otherwise erase it.
    const chunkFinishReason = parsed.choices?.[0]?.finish_reason
    const refusalAlreadyCaptured = isRefusalFinishReason(finishReason)
    if (typeof chunkFinishReason === 'string' && chunkFinishReason.length > 0 && !refusalAlreadyCaptured) {
      finishReason = chunkFinishReason
    }
  }
  const requestController = new AbortController()
  const normalizedTimeout =
    typeof timeout === 'number' && Number.isFinite(timeout) && timeout > 0 ? Math.floor(timeout) : undefined
  let timeoutId: ReturnType<typeof setTimeout> | null = null
  let timedOut = false
  const onExternalAbort = () => {
    requestController.abort()
  }

  if (signal) {
    if (signal.aborted) {
      requestController.abort()
    } else {
      signal.addEventListener('abort', onExternalAbort, { once: true })
    }
  }

  if (normalizedTimeout != null) {
    timeoutId = setTimeout(() => {
      timedOut = true
      requestController.abort()
    }, normalizedTimeout)
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Session-ID': sessionId,
        'X-Client-ID': clientId
      },
      body: JSON.stringify(body),
      signal: requestController.signal
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`API request failed with status ${response.status}: ${errorText}`)
    }

    // Expose run id for resumable runs via response header
    const runIdHeader = response.headers.get('x-run-id')
    if (callbacks.onMeta && runIdHeader) {
      callbacks.onMeta({ runId: runIdHeader })
    }

    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('Failed to get response reader')
    }

    const decoder = new TextDecoder()

    // Buffered SSE parser to avoid dropping partial frames
    let buffer = ''
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      // Normalize CRLF to LF
      buffer = buffer.replace(/\r\n/g, '\n')

      // Extract complete SSE frames separated by a blank line
      let sepIndex = buffer.indexOf('\n\n')
      while (sepIndex !== -1) {
        const frame = buffer.slice(0, sepIndex)
        buffer = buffer.slice(sepIndex + 2)

        // Collect multi-line data fields per SSE spec
        const dataPayload = frame
          .split('\n')
          .filter((line) => line.startsWith('data:'))
          .map((line) => {
            // Allow optional space after "data:"
            return line.slice(5).replace(/^\s/, '')
          })
          .join('\n')

        if (dataPayload) {
          if (dataPayload === '[DONE]') {
            // Ignore here; completion handled outside the loop
          } else {
            try {
              processParsedFrame(JSON.parse(dataPayload))
            } catch (e) {
              console.error('Failed to parse stream chunk:', dataPayload, e)
            }
          }
        }

        sepIndex = buffer.indexOf('\n\n')
      }
    }

    // Flush any remaining decoder tail and process leftover frames
    try {
      const tail = decoder.decode()
      if (tail) {
        buffer += tail.replace(/\r\n/g, '\n')
      }

      // Process any remaining complete frames
      let sepIndexTail = buffer.indexOf('\n\n')
      while (sepIndexTail !== -1) {
        const frame = buffer.slice(0, sepIndexTail)
        buffer = buffer.slice(sepIndexTail + 2)

        const dataPayload = frame
          .split('\n')
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).replace(/^\s/, ''))
          .join('\n')

        if (dataPayload && dataPayload !== '[DONE]') {
          try {
            processParsedFrame(JSON.parse(dataPayload))
          } catch (e) {
            console.error('Failed to parse stream chunk (tail frame):', dataPayload, e)
          }
        }

        sepIndexTail = buffer.indexOf('\n\n')
      }

      // If there is a leftover partial frame without a terminating blank line, try to parse it carefully
      const trimmed = buffer.trim()
      if (trimmed.startsWith('data:')) {
        const dataPayload = trimmed
          .split('\n')
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).replace(/^\s/, ''))
          .join('\n')

        if (dataPayload && dataPayload !== '[DONE]') {
          try {
            processParsedFrame(JSON.parse(dataPayload))
          } catch (e) {
            // It's okay if this fails; the server should have sent a complete frame.
            // We only attempt this to ensure we don't miss a last tiny fragment.
            console.error('Failed to parse leftover partial frame:', dataPayload, e)
          }
        }
      }
    } catch (e) {
      // Ignore tail flush errors; proceed to complete
    }

    callbacks.onComplete(finalContent, usage, finishReason)
  } catch (error) {
    if (isAbortError(error)) {
      if (timedOut && normalizedTimeout != null) {
        callbacks.onError(new Error(`Streaming request timed out after ${normalizedTimeout}ms`))
        return
      }
      callbacks.onCancel && callbacks.onCancel()
      return
    }
    console.error('Streaming request failed:', error)
    callbacks.onError(error instanceof Error ? error : new Error('An unknown streaming error occurred'))
  } finally {
    if (timeoutId != null) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
    if (signal) {
      signal.removeEventListener('abort', onExternalAbort)
    }
  }
}

function createHandleWithRunId(
  promise: Promise<void>,
  controller: AbortController,
  getRunId: () => string | undefined
): StreamingRequestHandle {
  return {
    controller,
    promise,
    get runId() {
      return getRunId()
    }
  }
}

export function startStreamingCompletionRequest(request: StreamingCompletionApiRequest): StreamingRequestHandle {
  const controller = createStreamingController(request.signal)
  let capturedRunId: string | undefined
  const promise = streamingCompletionRequest({
    ...request,
    signal: controller.signal,
    onMeta: (meta) => {
      if (meta.runId) capturedRunId = meta.runId
      request.onMeta && request.onMeta(meta)
    }
  })

  return createHandleWithRunId(promise, controller, () => capturedRunId)
}

export async function streamingCompletionRequest({
  messages,
  modelConfig,
  sessionId,
  onChunk,
  onComplete,
  onError,
  onCancel,
  onMeta,
  options,
  providerState,
  signal,
  timeout,
  requestTimeoutMs,
  maxRetries
}: StreamingCompletionApiRequest): Promise<void> {
  // Strip UI-only metadata before sending
  const messagesForApi = sanitizeConversationMessagesForApi(messages)
  const sanitizedProviderState = sanitizeProviderStateForApi(providerState)

  const body = {
    model: modelConfig.name,
    messages: messagesForApi, // Use stripped messages
    stream: true,
    ...(sessionId ? { sessionId } : {}),
    options: { ...modelConfig.options, ...options },
    ...(sanitizedProviderState ? { providerState: sanitizedProviderState } : {}),
    ...(requestTimeoutMs !== undefined ? { requestTimeoutMs } : {}),
    ...(maxRetries !== undefined ? { maxRetries } : {})
  }
  await handleStreamingRequest('/v1/chat/completions', body, { onChunk, onComplete, onError, onCancel, onMeta }, timeout, signal, sessionId)
}

export function startStreamingAgentCompletionRequest(request: StreamingAgentCompletionApiRequest): StreamingRequestHandle {
  const controller = createStreamingController(request.signal)
  let capturedRunId: string | undefined
  const promise = streamingAgentCompletionRequest({
    ...request,
    signal: controller.signal,
    onMeta: (meta) => {
      if (meta.runId) capturedRunId = meta.runId
      request.onMeta && request.onMeta(meta)
    }
  })

  return createHandleWithRunId(promise, controller, () => capturedRunId)
}

export async function streamingAgentCompletionRequest({
  messages,
  name,
  sessionId,
  onChunk,
  onComplete,
  onError,
  onCancel,
  onMeta,
  providerState,
  signal,
  timeout,
  requestTimeoutMs,
  maxRetries
}: StreamingAgentCompletionApiRequest): Promise<void> {
  // Strip UI-only metadata before sending
  const messagesForApi = sanitizeConversationMessagesForApi(messages)
  const sanitizedProviderState = sanitizeProviderStateForApi(providerState)

  const body = {
    model: name,
    messages: messagesForApi, // Use stripped messages
    stream: true,
    ...(sessionId ? { sessionId } : {}),
    ...(sanitizedProviderState ? { providerState: sanitizedProviderState } : {}),
    ...(requestTimeoutMs !== undefined ? { requestTimeoutMs } : {}),
    ...(maxRetries !== undefined ? { maxRetries } : {})
  }
  await handleStreamingRequest('/v1/chat/completions', body, { onChunk, onComplete, onError, onCancel, onMeta }, timeout, signal, sessionId)
}

// get agents
export async function getAgents() {
  const response = await requestUrl<AgentConfigs>({
    method: 'GET',
    url: '/v1/core/agents'
  })
  return response
}

type AddAgentApiPayload = Omit<NewAgentRequest, 'toolOptions'> & {
  tools?: string[]
  selectedFunctions?: { [serverName: string]: string[] }
  previousName?: string
  overwrite: boolean
}

// add agent
export async function addAgent(agentConfig: NewAgentRequest | AgentConfig, previousName?: string) {
  // Transform the data to match API expectations, without using 'any'
  const { toolOptions, ...rest } = agentConfig
  const apiPayload: AddAgentApiPayload = {
    ...rest,
    ...(previousName ? { previousName } : {}),
    overwrite: true
  }

  // If toolOptions exists, extract userTools and selectedFunctions
  if (toolOptions) {
    apiPayload.tools = toolOptions.userTools
    if (toolOptions.selectedFunctions) {
      apiPayload.selectedFunctions = toolOptions.selectedFunctions
    }
  }

  const response = await requestUrl<{ status: string; message: string; data: { id: string; name: string } }>({
    method: 'POST',
    url: '/v1/core/add/agent',
    data: apiPayload
  })
  return response
}

// delete agent
export async function deleteAgent(agentName: string) {
  const requestData: DeleteAgentRequest = { name: agentName }
  const response = await requestUrl<{ status: string; message: string }>({
    method: 'POST',
    url: '/v1/core/delete/agent',
    data: requestData
  })
  return response
}

// --- Agent Publishing ---

export async function toggleAgentPublish(agentId: string, agentName: string) {
  return requestUrl<any>({
    method: 'POST',
    url: '/v1/core/agents/publish',
    data: { agentId, agentName }
  });
}

export async function getPublishedAgents() {
  return requestUrl<any>({
    method: 'GET',
    url: '/v1/core/agents/published'
  });
}

export async function getSharedAgents() {
  return requestUrl<any>({
    method: 'GET',
    url: '/v1/core/agents/shared-with-me'
  });
}

export async function getPublicAgent(username: string, slug: string) {
  return requestUrl<{ success: boolean; data: PublicAgentDetails }>({
    method: 'GET',
    url: `/v1/public/agent/${username}/${slug}`
  });
}

export async function chatWithPublicAgent(username: string, slug: string, messages: ModelMessage[], stream = false) {
  return requestUrl<GenerateResult>({
    method: 'POST',
    url: `/v1/public/agent/${username}/${slug}/chat`,
    data: { messages, stream }
  });
}

export interface StreamingPublicChatCallbacks {
  onChunk: (chunk: string) => void;
  /** `finishReason` is the terminal finish reason observed on the stream, or null if none was sent. */
  onComplete: (finalContent: string, usage?: TokenUsage, finishReason?: string | null) => void;
  onError: (error: Error) => void;
  onCancel?: () => void;
  onMeta?: (meta: { runId?: string }) => void;
  /** Invoked when the server discards the in-progress assistant turn and retries it (PTC recovery). */
  onReset?: () => void;
}

export function startStreamingChatWithPublicAgent(
  username: string,
  slug: string,
  chatId: string,
  messages: ModelMessage[],
  callbacks: StreamingPublicChatCallbacks,
  signal?: AbortSignal
): StreamingRequestHandle {
  const controller = createStreamingController(signal)
  let capturedRunId: string | undefined
  const promise = streamingChatWithPublicAgent(
    username,
    slug,
    chatId,
    messages,
    {
      ...callbacks,
      onMeta: (meta: { runId?: string }) => {
        if (meta.runId) capturedRunId = meta.runId
        callbacks.onMeta && callbacks.onMeta(meta)
      }
    },
    controller.signal
  )

  return createHandleWithRunId(promise, controller, () => capturedRunId)
}

export async function streamingChatWithPublicAgent(
  username: string,
  slug: string,
  chatId: string,
  messages: ModelMessage[],
  callbacks: StreamingPublicChatCallbacks,
  signal?: AbortSignal
): Promise<void> {
  // Strip UI-only metadata before sending
  const messagesForApi = sanitizeConversationMessagesForApi(messages)

  const body = {
    chatId,
    messages: messagesForApi, // Use stripped messages
    stream: true,
  };
  await handleStreamingRequest(`/v1/public/agent/${username}/${slug}/chat`, body, callbacks, undefined, signal);
}
 
export interface StreamingPublicSpeakCallbacks {
  onTextDelta: (content: string) => void;
  onMessageStop: () => void;
  onAudioChunk: (b64: string) => void;
  onAudioStop: () => void;
  onError: (message: string) => void;
  onCancel?: () => void;
  onStatus?: (info: StatusEvent) => void;
  onRun?: (runId: string) => void;
  onSnapshot?: (snapshot: SnapshotEvent) => void;
  onUsage?: (usage: { promptTokens: number; completionTokens: number; totalTokens: number }) => void;
}

export function startStreamingPublicAgentSpeak(
  username: string,
  slug: string,
  chatId: string,
  messages: ModelMessage[],
  callbacks: StreamingPublicSpeakCallbacks,
  signal?: AbortSignal
): StreamingRequestHandle {
  const controller = createStreamingController(signal)
  let capturedRunId: string | undefined
  const promise = streamingPublicAgentSpeak(
    username,
    slug,
    chatId,
    messages,
    {
      ...callbacks,
      onRun: (runId: string) => {
        capturedRunId = runId
        callbacks.onRun && callbacks.onRun(runId)
      }
    },
    controller.signal
  )

  return createHandleWithRunId(promise, controller, () => capturedRunId)
}

/**
 * Stream public agent speak (text + audio multiplexed SSE)
 * Consumes /v1/public/agent/:username/:slug/speak and dispatches typed callbacks.
 */
export async function streamingPublicAgentSpeak(
  username: string,
  slug: string,
  chatId: string,
  messages: ModelMessage[],
  callbacks: StreamingPublicSpeakCallbacks,
  signal?: AbortSignal
): Promise<void> {
  try {
    // Strip UI-only metadata before sending
    const messagesForApi = sanitizeConversationMessagesForApi(messages)
    const { sessionId, clientId } = getState()

    const response = await fetch(`/v1/public/agent/${encodeURIComponent(username)}/${encodeURIComponent(slug)}/speak`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionId ? { 'X-Session-ID': sessionId } : {}),
        ...(clientId ? { 'X-Client-ID': clientId } : {})
      },
      body: JSON.stringify({ chatId, messages: messagesForApi, ttsOverrides: { responseFormat: 'opus' } }),
      signal
    })

    if (!response.ok || !response.body) {
      throw new Error(`HTTP ${response.status}`)
    }

    // Expose run id from response headers if present
    const runIdHeader = response.headers.get('x-run-id')
    if (callbacks.onRun && runIdHeader) {
      callbacks.onRun(runIdHeader)
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    // Debug instrumentation for speak stream
    let speakTextTotal = 0
    let speakAfterStop = false
    let audioStopCalled = false

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n')

      let sep = buffer.indexOf('\n\n')
      while (sep !== -1) {
        const frame = buffer.slice(0, sep)
        buffer = buffer.slice(sep + 2)

        const dataPayload = frame
          .split('\n')
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).replace(/^\s/, ''))
          .join('\n')

        if (dataPayload) {
          if (dataPayload === '[DONE]') {
            // end of stream
          } else {
            try {
              const parsed: unknown = JSON.parse(dataPayload)
              if (isRunEvent(parsed)) {
                callbacks.onRun && callbacks.onRun((parsed as any).runId)
                // eslint-disable-next-line no-console
                console.log('[SPEAK DEBUG] run', { runId: (parsed as any).runId })
              } else if (isSnapshotEvent(parsed)) {
                callbacks.onSnapshot && callbacks.onSnapshot(parsed as SnapshotEvent)
                // eslint-disable-next-line no-console
                console.log('[SPEAK DEBUG] snapshot', { contentLen: (parsed as any).content?.length ?? 0, status: (parsed as any).status })
              } else if (isStatusEvent(parsed)) {
                callbacks.onStatus && callbacks.onStatus(parsed)
                // eslint-disable-next-line no-console
                console.log('[SPEAK DEBUG] status', parsed)
              } else if (isTextDeltaEvent(parsed)) {
                const delta = parsed.content
                speakTextTotal += typeof delta === 'string' ? delta.length : 0
                // eslint-disable-next-line no-console
                console.log('[SPEAK DEBUG] text_delta', { len: delta?.length ?? 0, total: speakTextTotal, afterStop: speakAfterStop })
                callbacks.onTextDelta(delta)
              } else if (isMessageStopEvent(parsed)) {
                // eslint-disable-next-line no-console
                console.log('[SPEAK DEBUG] message_stop', { totalText: speakTextTotal })
                speakAfterStop = true
                callbacks.onMessageStop()
              } else if (isAudioChunkEvent(parsed)) {
                // eslint-disable-next-line no-console
                console.log('[SPEAK DEBUG] audio_chunk', { b64Len: parsed.b64?.length ?? 0 })
                callbacks.onAudioChunk(parsed.b64)
              } else if (isAudioStopEvent(parsed)) {
                // eslint-disable-next-line no-console
                console.log('[SPEAK DEBUG] audio_stop')
                audioStopCalled = true
                callbacks.onAudioStop()
              } else if (isUsageEvent(parsed)) {
                // eslint-disable-next-line no-console
                console.log('[SPEAK DEBUG] usage', parsed)
                if (callbacks.onUsage) {
                  callbacks.onUsage({
                    promptTokens: parsed.promptTokens,
                    completionTokens: parsed.completionTokens,
                    totalTokens: parsed.totalTokens
                  })
                }
              } else if (isErrorEvent(parsed)) {
                // eslint-disable-next-line no-console
                console.warn('[SPEAK DEBUG] error', parsed)
                callbacks.onError(parsed.message)
              } else {
                // eslint-disable-next-line no-console
                console.log('[SPEAK DEBUG] unknown_frame', parsed)
              }
            } catch {
              // ignore invalid JSON frames safely
            }
          }
        }

        sep = buffer.indexOf('\n\n')
      }
    }

    // Flush decoder tail and leftover frames to avoid dropping last text_delta
    try {
      const tail = decoder.decode()
      if (tail) {
        buffer += tail.replace(/\r\n/g, '\n')
      }

      // Process any remaining complete frames
      let sepTail = buffer.indexOf('\n\n')
      while (sepTail !== -1) {
        const frame = buffer.slice(0, sepTail)
        buffer = buffer.slice(sepTail + 2)

        const dataPayload = frame
          .split('\n')
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).replace(/^\s/, ''))
          .join('\n')

        if (dataPayload && dataPayload !== '[DONE]') {
          try {
            const parsed: unknown = JSON.parse(dataPayload)
            if (isRunEvent(parsed)) {
              callbacks.onRun && callbacks.onRun((parsed as any).runId)
            } else if (isSnapshotEvent(parsed)) {
              callbacks.onSnapshot && callbacks.onSnapshot(parsed as SnapshotEvent)
            } else if (isStatusEvent(parsed)) {
              callbacks.onStatus && callbacks.onStatus(parsed)
            } else if (isTextDeltaEvent(parsed)) {
              const delta = parsed.content
              speakTextTotal += typeof delta === 'string' ? delta.length : 0
              // eslint-disable-next-line no-console
              console.log('[SPEAK DEBUG] text_delta (tail)', { len: delta?.length ?? 0, total: speakTextTotal })
              callbacks.onTextDelta(delta)
            } else if (isMessageStopEvent(parsed)) {
              speakAfterStop = true
              callbacks.onMessageStop()
            } else if (isAudioChunkEvent(parsed)) {
              callbacks.onAudioChunk(parsed.b64)
            } else if (isAudioStopEvent(parsed)) {
              // eslint-disable-next-line no-console
              console.log('[SPEAK DEBUG] audio_stop (tail)')
              audioStopCalled = true
              callbacks.onAudioStop()
            } else if (isUsageEvent(parsed)) {
              // eslint-disable-next-line no-console
              console.log('[SPEAK DEBUG] usage (tail)', parsed)
              if (callbacks.onUsage) {
                callbacks.onUsage({
                  promptTokens: parsed.promptTokens,
                  completionTokens: parsed.completionTokens,
                  totalTokens: parsed.totalTokens
                })
              }
            } else if (isErrorEvent(parsed)) {
              callbacks.onError(parsed.message)
            }
          } catch {
            // ignore tail JSON errors
          }
        }

        sepTail = buffer.indexOf('\n\n')
      }

      // Attempt to parse any leftover partial 'data:' lines without terminator
      const trimmed = buffer.trim()
      if (trimmed.startsWith('data:')) {
        const dataPayload = trimmed
          .split('\n')
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).replace(/^\s/, ''))
          .join('\n')

        if (dataPayload && dataPayload !== '[DONE]') {
          try {
            const parsed: unknown = JSON.parse(dataPayload)
            if (isTextDeltaEvent(parsed)) {
              const delta = parsed.content
              speakTextTotal += typeof delta === 'string' ? delta.length : 0
              // eslint-disable-next-line no-console
              console.log('[SPEAK DEBUG] text_delta (partial)', { len: delta?.length ?? 0, total: speakTextTotal })
              callbacks.onTextDelta(delta)
            } else if (isUsageEvent(parsed)) {
              // eslint-disable-next-line no-console
              console.log('[SPEAK DEBUG] usage (partial)', parsed)
              if (callbacks.onUsage) {
                callbacks.onUsage({
                  promptTokens: parsed.promptTokens,
                  completionTokens: parsed.completionTokens,
                  totalTokens: parsed.totalTokens
                })
              }
            } else if (isAudioStopEvent(parsed)) {
              // eslint-disable-next-line no-console
              console.log('[SPEAK DEBUG] audio_stop (partial)')
              audioStopCalled = true
              callbacks.onAudioStop()
            } else if (isMessageStopEvent(parsed)) {
              speakAfterStop = true
              callbacks.onMessageStop()
            }
          } catch {
            // best-effort parse only
          }
        }
      }
    } catch {
      // ignore tail flush errors
    }

    // Emergency cleanup: Ensure audio_stop is always called
    if (!audioStopCalled) {
      // eslint-disable-next-line no-console
      console.log('[SPEAK DEBUG] audio_stop (fallback - not received from server)')
      callbacks.onAudioStop()
    }
  } catch (error) {
    if (isAbortError(error)) {
      callbacks.onCancel && callbacks.onCancel()
      return
    }
    throw error
  }
}

export function subscribePublicRun(
  runId: string,
  callbacks: {
    onSnapshot?: (snapshot: { type: 'snapshot'; content: string; usage?: TokenUsage; status: 'running' | 'completed' | 'error'; updatedAt: number }) => void;
    onTextDelta?: (content: string) => void;
    onDone?: () => void;
    onError?: (message: string) => void;
  }
): () => void {
  const es = new EventSource(`/v1/public/runs/${encodeURIComponent(runId)}/stream`)
  es.onmessage = (e: MessageEvent) => {
    try {
      if (e.data === '[DONE]') {
        callbacks.onDone && callbacks.onDone()
        try { es.close() } catch { /* ignore */ }
        return
      }
      const parsed = JSON.parse(e.data)
      if (parsed?.type === 'snapshot') {
        callbacks.onSnapshot && callbacks.onSnapshot(parsed)
      } else if (parsed?.type === 'text_delta' && typeof parsed.content === 'string') {
        callbacks.onTextDelta && callbacks.onTextDelta(parsed.content)
      } else if (parsed?.type === 'error' && typeof parsed.message === 'string') {
        callbacks.onError && callbacks.onError(parsed.message)
      }
    } catch {
      // ignore malformed frames
    }
  }
  es.onerror = () => {
    callbacks.onError && callbacks.onError('run stream error')
    try { es.close() } catch { /* ignore */ }
  }
  return () => {
    try { es.close() } catch { /* ignore */ }
  }
}

export interface CancelStreamResponse {
  success: boolean
  found?: boolean
  cancelled?: boolean
  alreadyCancelled?: boolean
  message?: string
}

export async function cancelChatStream(sessionId: string, runId?: string): Promise<CancelStreamResponse | ApiErrorResponse> {
  const payload: { sessionId: string; runId?: string } = { sessionId }
  if (runId) payload.runId = runId

  return requestUrl<CancelStreamResponse>({
    method: 'POST',
    url: '/v1/chat/cancel',
    data: payload
  })
}

export async function cancelPublicRun(runId: string): Promise<CancelStreamResponse | ApiErrorResponse> {
  return requestUrl<CancelStreamResponse>({
    method: 'POST',
    url: `/v1/public/runs/${encodeURIComponent(runId)}/cancel`
  })
}

// Share management for published agents (owner authenticated)
export async function listPublishedAgentShares(ownerUsername: string, slug: string) {
  return requestUrl<{ success: boolean; data: AgentShareDTO[] }>({
    method: 'GET',
    url: `/v1/core/agents/${encodeURIComponent(ownerUsername)}/${encodeURIComponent(slug)}/shares`
  })
}

export async function sharePublishedAgent(
  ownerUsername: string,
  slug: string,
  recipient: string,
  editable: boolean = false
) {
  return requestUrl<{ success: boolean }>({
    method: 'POST',
    url: `/v1/core/agents/${encodeURIComponent(ownerUsername)}/${encodeURIComponent(slug)}/shares`,
    data: { recipient, editable }
  })
}

export async function revokePublishedAgentShare(
  ownerUsername: string,
  slug: string,
  recipientType: 'username' | 'email',
  recipient: string
) {
  return requestUrl<{ success: boolean }>({
    method: 'DELETE',
    url: `/v1/core/agents/${encodeURIComponent(ownerUsername)}/${encodeURIComponent(slug)}/shares`,
    data: { recipientType, recipient }
  })
}

// --- Shared agent edit (recipient with editable=true) ---

export interface SharedAgentUpdatePayload {
  description?: string
  icon?: string | null
  systemPrompt?: string
  systemPromptId?: string
  modelOptions?: { temperature?: number; maxTokens?: number }
  addToday?: boolean
  timezoneOffset?: string
  combineSystemPrompts?: boolean
  convertSystemPrompt?: boolean
  toolOptions?: AgentToolOptions
  embeddingOptions?: AgentEmbeddingOptions
  voice?: Partial<AgentVoiceConfig> | null
}

export interface SharedAgentOwnerToolsResponse {
  success: boolean
  servers: Array<{
    name: string
    displayName?: string
    description?: string
    enabled?: boolean
    installed?: boolean
    source?: string
  }>
  tools: Array<{ [serverName: string]: Array<{ name: string; description?: string; inputSchema?: unknown }> }>
}

export interface SharedAgentOwnerEmbeddingsResponse {
  success: boolean
  vectorStores: Array<{ name: string; safeName: string; embeddingModel: string }>
  embeddingModels: Array<{ id?: string; name: string; modelName: string; providerKey: string }>
}

export interface SharedAgentOwnerVoicesResponse {
  success: boolean
  voices: Array<{
    id: string
    name: string
    providerKey: string
    responseFormat?: string
    speed?: number
  }>
}

export async function getSharedAgentConfig(ownerUsername: string, slug: string) {
  return requestUrl<{ success: boolean; data: AgentConfig; modelName: string }>({
    method: 'GET',
    url: `/v1/core/agents/${encodeURIComponent(ownerUsername)}/${encodeURIComponent(slug)}/config`
  })
}

export async function updateSharedAgentConfig(
  ownerUsername: string,
  slug: string,
  payload: SharedAgentUpdatePayload
) {
  return requestUrl<{ status: string; message: string; data: { id: string; name: string } }>({
    method: 'PUT',
    url: `/v1/core/agents/${encodeURIComponent(ownerUsername)}/${encodeURIComponent(slug)}/config`,
    data: payload
  })
}

export async function getSharedAgentOwnerTools(ownerUsername: string, slug: string) {
  return requestUrl<SharedAgentOwnerToolsResponse>({
    method: 'GET',
    url: `/v1/core/agents/${encodeURIComponent(ownerUsername)}/${encodeURIComponent(slug)}/owner-tools`
  })
}

export async function getSharedAgentOwnerEmbeddings(ownerUsername: string, slug: string) {
  return requestUrl<SharedAgentOwnerEmbeddingsResponse>({
    method: 'GET',
    url: `/v1/core/agents/${encodeURIComponent(ownerUsername)}/${encodeURIComponent(slug)}/owner-embeddings`
  })
}

export async function getSharedAgentOwnerVoices(ownerUsername: string, slug: string) {
  return requestUrl<SharedAgentOwnerVoicesResponse>({
    method: 'GET',
    url: `/v1/core/agents/${encodeURIComponent(ownerUsername)}/${encodeURIComponent(slug)}/owner-voices`
  })
}

// Public Agent Chat Sessions (Authenticated)
export async function listPublicSessions(username: string, slug: string, limit?: number, offset?: number) {
  const qs: string[] = []
  if (limit != null) qs.push(`limit=${encodeURIComponent(String(limit))}`)
  if (offset != null) qs.push(`offset=${encodeURIComponent(String(offset))}`)
  const suffix = qs.length ? `?${qs.join('&')}` : ''
  return requestUrl<{ success: boolean; data: PublicChatSession[] }>({
    method: 'GET',
    url: `/v1/public/agent/${encodeURIComponent(username)}/${encodeURIComponent(slug)}/sessions${suffix}`
  })
}

export async function getPublicSession(username: string, slug: string, id: string) {
  return requestUrl<{ success: boolean; data?: PublicChatSession; message?: string }>({
    method: 'GET',
    url: `/v1/public/agent/${encodeURIComponent(username)}/${encodeURIComponent(slug)}/sessions/${encodeURIComponent(id)}`
  })
}

export async function savePublicSession(username: string, slug: string, session: PublicChatSession) {
  return requestUrl<{ success: boolean; message?: string }>({
    method: 'POST',
    url: `/v1/public/agent/${encodeURIComponent(username)}/${encodeURIComponent(slug)}/sessions`,
    data: {
      ...session,
      messages: filterConversationMessages(session.messages)
    }
  })
}

export async function updatePublicSessionTitle(
  username: string,
  slug: string,
  id: string,
  title: string,
  updatedAt: number
) {
  return requestUrl<{ success: boolean; title?: string; updatedAt?: number; message?: string }>({
    method: 'PATCH',
    url: `/v1/public/agent/${encodeURIComponent(username)}/${encodeURIComponent(slug)}/sessions/${encodeURIComponent(id)}/title`,
    data: { title, updatedAt }
  })
}

export async function deletePublicSession(username: string, slug: string, id: string) {
  return requestUrl<{ success: boolean; message?: string }>({
    method: 'DELETE',
    url: `/v1/public/agent/${encodeURIComponent(username)}/${encodeURIComponent(slug)}/sessions/${encodeURIComponent(id)}`
  })
}

// Workflow Configs + Runs

export async function listWorkflowConfigs() {
  return requestUrl<{ success: boolean; data: WorkflowConfig[] }>({
    method: 'GET',
    url: '/v1/core/workflows'
  })
}

export async function createWorkflowConfig(config: WorkflowConfig) {
  return requestUrl<{ success: boolean; data: WorkflowConfig }>({
    method: 'POST',
    url: '/v1/core/workflows',
    data: config
  })
}

export async function updateWorkflowConfig(id: string, config: WorkflowConfig) {
  return requestUrl<{ success: boolean; data: WorkflowConfig }>({
    method: 'PUT',
    url: `/v1/core/workflows/${encodeURIComponent(id)}`,
    data: config
  })
}

export async function deleteWorkflowConfig(id: string) {
  return requestUrl<{ success: boolean; message?: string }>({
    method: 'DELETE',
    url: `/v1/core/workflows/${encodeURIComponent(id)}`
  })
}

export async function createWorkflowRun(workflowId: string) {
  return requestUrl<{ success: boolean; runId: string; data: WorkflowRunRecord }>({
    method: 'POST',
    url: '/v1/core/workflow-runs',
    data: { workflowId }
  })
}

export async function getWorkflowRun(runId: string) {
  return requestUrl<{ success: boolean; data: WorkflowRunRecord }>({
    method: 'GET',
    url: `/v1/core/workflow-runs/${encodeURIComponent(runId)}`
  })
}

export async function getHydratedWorkflowRun(runId: string) {
  return requestUrl<{ success: boolean; data: WorkflowRunHydratedRecord }>({
    method: 'GET',
    url: `/v1/core/workflow-runs/${encodeURIComponent(runId)}/hydrated`
  })
}

export async function listWorkflowRunsForWorkflow(workflowId: string, limit?: number, offset?: number) {
  const qs: string[] = []
  if (limit != null) qs.push(`limit=${encodeURIComponent(String(limit))}`)
  if (offset != null) qs.push(`offset=${encodeURIComponent(String(offset))}`)
  const suffix = qs.length ? `?${qs.join('&')}` : ''
  return requestUrl<{ success: boolean; data: WorkflowRunRecord[] }>({
    method: 'GET',
    url: `/v1/core/workflows/${encodeURIComponent(workflowId)}/runs${suffix}`
  })
}

export function subscribeWorkflowRun(
  runId: string,
  callbacks: {
    onSnapshot?: (record: WorkflowRunRecord) => void
    onEvent?: (event: any) => void
    onDone?: () => void
    onError?: (message: string) => void
  }
): () => void {
  const es = new EventSource(`/v1/core/workflow-runs/${encodeURIComponent(runId)}/stream`)
  es.onmessage = (e: MessageEvent) => {
    try {
      if (e.data === '[DONE]') {
        callbacks.onDone && callbacks.onDone()
        try { es.close() } catch { /* ignore */ }
        return
      }
      const parsed = JSON.parse(e.data)
      if (parsed?.type === 'snapshot' && parsed.record) {
        callbacks.onSnapshot && callbacks.onSnapshot(parsed.record as WorkflowRunRecord)
      } else {
        callbacks.onEvent && callbacks.onEvent(parsed)
      }
    } catch {
      // ignore malformed frames
    }
  }
  es.onerror = () => {
    callbacks.onError && callbacks.onError('workflow run stream error')
    try { es.close() } catch { /* ignore */ }
  }
  return () => {
    try { es.close() } catch { /* ignore */ }
  }
}

export async function cancelWorkflowRun(runId: string) {
  return requestUrl<{ success: boolean; found?: boolean; cancelled?: boolean; alreadyCancelled?: boolean }>({
    method: 'POST',
    url: `/v1/core/workflow-runs/${encodeURIComponent(runId)}/cancel`
  })
}

// Factory Configs + Runs + Schedules

export async function listFactories() {
  return requestUrl<{ success: boolean; data: FactoryConfig[] }>({
    method: 'GET',
    url: '/v1/core/factories'
  })
}

export async function getFactory(id: string) {
  return requestUrl<{ success: boolean; data: FactoryConfig }>({
    method: 'GET',
    url: `/v1/core/factories/${encodeURIComponent(id)}`
  })
}

export async function createFactory(config: FactoryConfig) {
  return requestUrl<{ success: boolean; data: FactoryConfig }>({
    method: 'POST',
    url: '/v1/core/factories',
    data: config
  })
}

export async function updateFactory(id: string, config: FactoryConfig) {
  return requestUrl<{ success: boolean; data: FactoryConfig }>({
    method: 'PUT',
    url: `/v1/core/factories/${encodeURIComponent(id)}`,
    data: config
  })
}

export async function deleteFactory(id: string) {
  return requestUrl<{ success: boolean; message?: string }>({
    method: 'DELETE',
    url: `/v1/core/factories/${encodeURIComponent(id)}`
  })
}

export async function createFactoryRun(factoryConfigId: string, initialCarryPayload?: string) {
  return requestUrl<{ success: boolean; runId: string; data: FactoryRunRecord }>({
    method: 'POST',
    url: '/v1/core/factory-runs',
    data: { factoryConfigId, ...(initialCarryPayload != null ? { initialCarryPayload } : {}) }
  })
}

export async function getFactoryRun(runId: string) {
  return requestUrl<{ success: boolean; data: FactoryRunRecord }>({
    method: 'GET',
    url: `/v1/core/factory-runs/${encodeURIComponent(runId)}`
  })
}

export async function listFactoryRunsForFactory(factoryId: string, limit?: number, offset?: number) {
  return requestUrl<{ success: boolean; data: FactoryRunRecord[] }>({
    method: 'GET',
    url: `/v1/core/factories/${encodeURIComponent(factoryId)}/runs${toQuery({ limit, offset })}`
  })
}

export async function listFactoryStepRuns(runId: string, limit?: number, offset?: number) {
  return requestUrl<{ success: boolean; data: FactoryStepRunRecord[] }>({
    method: 'GET',
    url: `/v1/core/factory-runs/${encodeURIComponent(runId)}/steps${toQuery({ limit, offset })}`
  })
}

export async function getFactoryStepRun(runId: string, stepRunId: string, hydrated = false) {
  return requestUrl<{ success: boolean; data: FactoryStepRunRecord | HydratedFactoryStepRunRecord }>({
    method: 'GET',
    url: `/v1/core/factory-runs/${encodeURIComponent(runId)}/steps/${encodeURIComponent(stepRunId)}${hydrated ? '/hydrated' : ''}`
  })
}

export async function getHydratedFactoryStepRun(runId: string, stepRunId: string) {
  return requestUrl<{ success: boolean; data: HydratedFactoryStepRunRecord }>({
    method: 'GET',
    url: `/v1/core/factory-runs/${encodeURIComponent(runId)}/steps/${encodeURIComponent(stepRunId)}/hydrated`
  })
}

export async function pauseFactoryRun(runId: string) {
  return requestUrl<FactoryRunActionResponse>({
    method: 'POST',
    url: `/v1/core/factory-runs/${encodeURIComponent(runId)}/pause`
  })
}

export async function resumeFactoryRun(runId: string) {
  return requestUrl<FactoryRunActionResponse>({
    method: 'POST',
    url: `/v1/core/factory-runs/${encodeURIComponent(runId)}/resume`
  })
}

export async function cancelFactoryRun(runId: string) {
  return requestUrl<{ success: boolean; found?: boolean; cancelled?: boolean; alreadyCancelled?: boolean }>({
    method: 'POST',
    url: `/v1/core/factory-runs/${encodeURIComponent(runId)}/cancel`
  })
}

export function subscribeFactoryRun(
  runId: string,
  callbacks: {
    onSnapshot?: (record: FactoryRunRecord) => void
    onEvent?: (event: any) => void
    onDone?: () => void
    onError?: (message: string) => void
  }
): () => void {
  const es = new EventSource(`/v1/core/factory-runs/${encodeURIComponent(runId)}/stream`)
  es.onmessage = (e: MessageEvent) => {
    try {
      if (e.data === '[DONE]') {
        callbacks.onDone && callbacks.onDone()
        try { es.close() } catch { /* ignore */ }
        return
      }
      const parsed = JSON.parse(e.data)
      if (parsed?.type === 'snapshot' && parsed.record) {
        callbacks.onSnapshot && callbacks.onSnapshot(parsed.record as FactoryRunRecord)
      } else {
        callbacks.onEvent && callbacks.onEvent(parsed)
      }
    } catch {
      /* ignore malformed frames */
    }
  }
  es.onerror = () => {
    callbacks.onError && callbacks.onError('factory run stream error')
    try { es.close() } catch { /* ignore */ }
  }
  return () => {
    try { es.close() } catch { /* ignore */ }
  }
}

export async function listFactorySchedules() {
  return requestUrl<{ success: boolean; data: FactoryScheduleRecord[] }>({
    method: 'GET',
    url: '/v1/core/factory-schedules'
  })
}

export async function createFactorySchedule(payload: NewFactorySchedulePayload) {
  return requestUrl<{ success: boolean; data: FactoryScheduleRecord }>({
    method: 'POST',
    url: '/v1/core/factory-schedules',
    data: payload
  })
}

export async function updateFactorySchedule(id: string, payload: Partial<FactoryScheduleRecord>) {
  return requestUrl<{ success: boolean; data: FactoryScheduleRecord }>({
    method: 'PUT',
    url: `/v1/core/factory-schedules/${encodeURIComponent(id)}`,
    data: payload
  })
}

export async function deleteFactorySchedule(id: string) {
  return requestUrl<{ success: boolean }>({
    method: 'DELETE',
    url: `/v1/core/factory-schedules/${encodeURIComponent(id)}`
  })
}

export async function toggleFactorySchedule(id: string) {
  return requestUrl<{ success: boolean; data: FactoryScheduleRecord }>({
    method: 'POST',
    url: `/v1/core/factory-schedules/${encodeURIComponent(id)}/toggle`
  })
}

/**
 * Vector Stores (Keep existing functions as they interact with different backend logic)
 */

// get vector stores

export async function listVectorStores() {
  return requestUrl<VectorStoreListResponse>({
    method: 'GET',
    url: '/v1/vector_stores'
  })
}

// create vector store

export async function createVectorStore(
  name: string,
  fileIds: string[],
  embeddingModelName?: string,
  chunking_strategy?: StaticChunkStrategy,
  sseSessionId?: string,
  enhancePDF?: boolean
) {
  return requestUrl<VectorStore>({
    method: 'POST',
    url: '/v1/vector_stores',
    data: {
      file_ids: fileIds,
      name: name,
      embeddingModelName,
      chunking_strategy,
      sseSessionId,
      enhancePDF
    }
  })
}

// get vector store

export async function getVectorStore(id: string) {
  return requestUrl<VectorStoreResponse>({
    method: 'GET',
    url: `/v1/vector_stores/${id}`
  })
}

// delete vector store

export async function deleteVectorStore(id: string) {
  return requestUrl<DeleteResponse>({
    method: 'DELETE',
    url: `/v1/vector_stores/${id}`
  })
}

export async function cancelVectorStoreEmbedding(sessionId: string) {
  return requestUrl<{ success: boolean; message: string }>({
    method: 'POST',
    url: '/v1/vector_stores/cancel',
    data: { sessionId }
  })
}

// get vector store files

export async function listVectorStoreFiles(id: string) {
  return requestUrl<VectorStoreFilesResponse>({
    method: 'GET',
    url: `/v1/vector_stores/${id}/files`
  })
}

// get vector store file

export async function getVectorStoreFile(id: string, fileId: string) {
  return requestUrl<VectorStoreFileResponse>({
    method: 'GET',
    url: `/v1/vector_stores/${id}/files/${fileId}`
  })
}

// get detailed files for a vector store (for lazy loading)
export async function getVectorStoreFilesDetailed(id: string) {
  return requestUrl<{ success: boolean; vectorStoreId: string; files: UserVectorFile[] }>({
    method: 'GET',
    url: `/v1/vector_stores/${id}/file-details`
  })
}

// add vector store file
// (file must be uploaded first with /v1/files)

export async function addVectorStoreFile(vectorStoreId: string, fileId: string, chunkStrategy?: StaticChunkStrategy) {
  const data: VectorStoreFileRequest = {
    file_id: fileId,
    chunking_strategy: chunkStrategy == null ? { type: 'auto' } : chunkStrategy
  }
  return requestUrl<VectorStoreFile>({
    method: 'POST',
    url: `/v1/vector_stores/${vectorStoreId}/files`,
    data
  })
}

// list all files

export async function listUserFiles() {
  return requestUrl<ListUserFilesResponse>({
    method: 'GET',
    url: `/v1/files`
  })
}

// add files

export async function addUserFile(
  file: File,
  purpose: string = 'assistants',
  relativePath?: string,
  collectionName?: string
) {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('filename', file.name)

  if (purpose) {
    formData.append('purpose', purpose)
  }

  // Add relative path if provided
  if (relativePath) {
    formData.append('relativePath', relativePath)
  }

  // Add collection name if provided
  if (collectionName) {
    formData.append('collectionName', collectionName)
  }

  return requestUrl<FileUpload[]>({
    method: 'POST',
    url: `/v1/files`,
    data: formData,
    file: true
    // headers: {
    //   'Content-Type': 'multipart/form-data'
    // }
  })
}

// get file by id

export async function getUserFileById(id: string) {
  return requestUrl<GetFileByIdResponse>({
    method: 'GET',
    url: `/v1/files/${id}`
  })
}

// delete file by id

export async function deleteUserFileById(id: string) {
  return requestUrl({
    method: 'DELETE',
    url: `/v1/files/${id}`
  })
}

// get file content

export async function getUserFileContent(id: string) {
  return requestUrl<string>({
    method: 'GET',
    url: `/v1/files/${id}/content`,
    responseType: 'text'
  })
}

// get vector stores and files
export async function getVectorStoresWithFiles() {
  return requestUrl<{ data: VectorStoreWithFiles[] }>({
    method: 'GET',
    url: `/v1/user-collections`
  })
}

// tools

export async function getTools() {
  return requestUrl<{ success: boolean; tools: Tool[] }>({
    method: 'GET',
    url: `/v1/core/tools`
  })
}

export async function getServers() {
  return requestUrl<{ success: boolean; servers: MCPServer[] }>({
    method: 'GET',
    url: `/v1/core/servers`
  })
}

export async function callMcpTool(payload: McpToolCallRequest) {
  return requestUrl<McpToolCallResponse>({
    method: 'POST',
    url: '/v1/mcp/tool/call',
    data: payload
  })
}

export async function getUserMcpServers() {
  return requestUrl<{ success: boolean; servers: UserVisibleMcpServer[] }>({
    method: 'GET',
    url: '/v1/mcp/servers'
  })
}

export async function getAdminMcpServers() {
  return requestUrl<{ success: boolean; servers: MCPServer[] }>({
    method: 'GET',
    url: '/v1/mcp/admin/servers'
  })
}

export async function getUserMcpServer(name: string) {
  return requestUrl<{ success: boolean; server: UserVisibleMcpServer }>({
    method: 'GET',
    url: `/v1/mcp/servers/${encodeURIComponent(name)}`
  })
}

export async function getUserMcpServerTools(name: string) {
  return requestUrl<{ success: boolean; tools: ToolMethod[] }>({
    method: 'GET',
    url: `/v1/mcp/servers/${encodeURIComponent(name)}/tools`
  })
}

export async function createExternalMcpServer(payload: CreateExternalMcpServerRequest) {
  return requestUrl<{ success: boolean; server: UserVisibleMcpServer }>({
    method: 'POST',
    url: '/v1/mcp/external-servers',
    data: payload
  })
}

export async function discoverExternalMcpOAuth(payload: DiscoverExternalMcpOAuthRequest) {
  return requestUrl<DiscoverExternalMcpOAuthResponse>({
    method: 'POST',
    url: '/v1/mcp/external-servers/discover',
    data: payload
  })
}

export async function installExternalMcpServer(name: string, payload: InstallExternalMcpServerRequest) {
  return requestUrl<{ success: boolean; server: UserVisibleMcpServer }>({
    method: 'POST',
    url: `/v1/mcp/servers/${encodeURIComponent(name)}/install`,
    data: payload
  })
}

export async function updateExternalMcpServerInstall(name: string, payload: UpdateExternalMcpServerInstallRequest) {
  return requestUrl<{ success: boolean; server: UserVisibleMcpServer }>({
    method: 'PUT',
    url: `/v1/mcp/servers/${encodeURIComponent(name)}/install`,
    data: payload
  })
}

export async function uninstallExternalMcpServer(name: string) {
  return requestUrl<{ success: boolean }>({
    method: 'DELETE',
    url: `/v1/mcp/servers/${encodeURIComponent(name)}/install`,
    data: {}
  })
}

export async function saveExternalMcpServerSecrets(name: string, payload: SaveExternalMcpServerSecretsRequest) {
  return requestUrl<{ success: boolean }>({
    method: 'POST',
    url: `/v1/mcp/servers/${encodeURIComponent(name)}/secrets`,
    data: payload
  })
}

export async function startExternalMcpOAuth(name: string, payload: StartExternalMcpOAuthRequest) {
  return requestUrl<StartExternalMcpOAuthResponse>({
    method: 'POST',
    url: `/v1/mcp/servers/${encodeURIComponent(name)}/oauth/start`,
    data: payload
  })
}

export async function refreshExternalMcpServer(name: string) {
  return requestUrl<{ success: boolean; server: UserVisibleMcpServer }>({
    method: 'POST',
    url: `/v1/mcp/servers/${encodeURIComponent(name)}/refresh`,
    data: {}
  })
}

// MCP Server management
export async function adminDeleteServer(name: string) {
  return requestUrl<{ success: boolean; message: string }>({
    method: 'DELETE',
    url: `/v1/mcp/admin/servers/${encodeURIComponent(name)}`
  })
}

export async function adminGetSharedServerDeleteImpact(name: string) {
  return requestUrl<AdminSharedServerDeleteImpactResponse>({
    method: 'GET',
    url: `/v1/mcp/admin/servers/${encodeURIComponent(name)}/delete-impact`
  })
}

export async function adminEnableServer(name: string) {
  return requestUrl<{ success: boolean; message: string }>({
    method: 'PUT',
    url: `/v1/mcp/admin/servers/${encodeURIComponent(name)}/enable`
  })
}

export async function adminDisableServer(name: string) {
  return requestUrl<{ success: boolean; message: string }>({
    method: 'PUT',
    url: `/v1/mcp/admin/servers/${encodeURIComponent(name)}/disable`
  })
}

// MCP Package management
export async function installPackage(request: InstallPackageRequest) {
  return requestUrl<InstallPackageResponse>({
    method: 'POST',
    url: `/v1/mcp/packages/install`,
    data: request
  })
}

export async function getPackages(checkUpdates: boolean = false) {
  return requestUrl<GetPackagesResponse>({
    method: 'GET',
    url: `/v1/mcp/packages${checkUpdates ? '?checkUpdates=true' : ''}`
  })
}

export async function getPackageByName(name: string, checkUpdates: boolean = false) {
  return requestUrl<GetPackageInfoResponse>({
    method: 'GET',
    url: `/v1/mcp/packages/by-name/${name}${checkUpdates ? '?checkUpdates=true' : ''}`
  })
}

export async function getPackageById(id: string, checkUpdates: boolean = false) {
  return requestUrl<GetPackageInfoResponse>({
    method: 'GET',
    url: `/v1/mcp/packages/by-id/${id}${checkUpdates ? '?checkUpdates=true' : ''}`
  })
}

export async function uninstallPackage(name: string) {
  return requestUrl<{ success: boolean; message: string }>({
    method: 'DELETE',
    url: `/v1/mcp/packages/${name}`
  })
}

export async function checkForUpdates(name?: string) {
  return requestUrl<GetPackageUpdatesResponse>({
    method: 'GET',
    url: `/v1/mcp/packages/updates${name ? `?name=${name}` : ''}`
  })
}

export async function upgradePackage(name: string, version?: string) {
  return requestUrl<UpgradePackageResponse>({
    method: 'PUT',
    url: `/v1/mcp/packages/${name}/upgrade`,
    data: version ? { version } : {}
  })
}

export async function upgradeAllPackages() {
  return requestUrl<UpgradeAllPackagesResponse>({
    method: 'PUT',
    url: `/v1/mcp/packages/upgrade-all`
  })
}

export async function getAvailableMcpServers(searchParam = '') {
  return requestUrl<{ success: boolean; servers: CatalogExternalMcpServer[] }>({
    method: 'GET',
    url: `/v1/mcp/catalog/servers${searchParam}`
  })
}

// Providers

export async function getProviders() {
  return requestUrl<
    { success: boolean; providers: Record<string, ProviderConfig> } | { success: false; error: string }
  >({
    method: 'GET',
    url: '/v1/core/providers'
  })
}

export async function addProvider(providerConfig: ProviderConfig) {
  return requestUrl<{ success: boolean; message: string }>({
    method: 'POST',
    url: '/v1/core/add/provider',
    data: providerConfig
  })
}

export async function deleteProvider(providerKey: ProviderKey) {
  const requestData: DeleteProviderRequest = { providerKey }
  return requestUrl<{ success: boolean; message: string }>({
    method: 'POST',
    url: '/v1/core/delete/provider',
    data: requestData
  })
}

// Authentication functions

export async function authenticate(username: string, password: string) {
  return requestUrl<SignInResponse>({
    method: 'POST',
    url: '/api/authenticate',
    data: { username, password }
  })
}

export async function checkAuth() {
  return requestUrl<AuthCheckResponse>({
    method: 'GET',
    url: '/api/authcheck'
  })
}

export async function signOut() {
  return requestUrl({
    method: 'GET',
    url: '/api/signout'
  })
}

export async function getStripeSubscription() {
  return requestUrl<StripeSubscriptionResponse>({
    method: 'GET',
    url: '/api/stripe/subscription'
  })
}

export async function refreshStripeSubscription() {
  return requestUrl<StripeSubscriptionResponse>({
    method: 'POST',
    url: '/api/stripe/subscription/refresh'
  })
}

export async function createStripePortalSession() {
  return requestUrl<StripePortalSessionResponse>({
    method: 'POST',
    url: '/api/stripe/portal-session'
  })
}

/**
 * User Settings (authenticated)
 */
export async function getUserSettings() {
  return requestUrl<GetUserSettingsResponseDTO>({
    method: 'GET',
    url: '/v1/core/user/settings'
  })
}

export async function updateUserSettings(data: UpdateUserSettingsRequestDTO) {
  return requestUrl<UpdateUserSettingsResponseDTO>({
    method: 'PUT',
    url: '/v1/core/user/settings',
    data
  })
}

export interface ConfigAgentProviderOption {
  providerKey: string
  modelName: string
}

export interface ConfigAgentStatusResponse {
  provisioned: boolean
  agentName: string | null
  ready: boolean
  availableProviders: ConfigAgentProviderOption[]
}

export async function getConfigAgentStatus(): Promise<ConfigAgentStatusResponse | null> {
  const response = await requestUrl<ConfigAgentStatusResponse>({
    method: 'GET',
    url: '/v1/core/config-agent/status'
  })

  if (isApiError(response)) {
    return null
  }
  return response
}

export async function provisionConfigAgent(providerKey: string): Promise<{ success: boolean; error?: string } | null> {
  const response = await requestUrl<{ success: boolean; error?: string }>({
    method: 'POST',
    url: '/v1/core/config-agent/provision',
    data: { providerKey }
  })

  if (isApiError(response)) {
    return null
  }
  return response
}

// Config agent chat history functions

export interface ConfigChatHistoryResponse {
  success: boolean
  id: string | null
  messages: ModelMessage[]
  providerState?: GenerateResultProviderState
}

export async function getConfigChatHistory(): Promise<ConfigChatHistoryResponse | null> {
  const response = await requestUrl<ConfigChatHistoryResponse>({
    method: 'GET',
    url: '/v1/core/config-agent/chat'
  })
  if (isApiError(response)) return null
  return response
}

export async function saveConfigChatHistory(
  id: string | null,
  messages: ModelMessage[],
  providerState?: GenerateResultProviderState
): Promise<{ success: boolean; id: string } | null> {
  const response = await requestUrl<{ success: boolean; id: string }>({
    method: 'POST',
    url: '/v1/core/config-agent/chat',
    data: { id, messages, providerState }
  })
  if (isApiError(response)) return null
  return response
}

export async function newConfigChatSession(): Promise<{ success: boolean; id: string } | null> {
  const response = await requestUrl<{ success: boolean; id: string }>({
    method: 'POST',
    url: '/v1/core/config-agent/chat/new'
  })
  if (isApiError(response)) return null
  return response
}

export async function uploadVideoToWorkflowRun(
  file: File,
  request: VideoToWorkflowUploadRequest
): Promise<VideoProcessingRunRecord | ApiErrorResponse> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('request', JSON.stringify(request))

  const response = await requestUrl<{ success: true; data: VideoProcessingRunRecord }>({
    method: 'POST',
    url: '/v1/core/video-to-workflow/runs/upload',
    data: formData,
    file: true
  })
  if (isApiError(response)) {
    return response
  }
  return response.data
}

export async function getVideoProcessingRun(runId: string): Promise<VideoProcessingRunRecord | ApiErrorResponse> {
  const response = await requestUrl<{ success: true; data: VideoProcessingRunRecord }>({
    method: 'GET',
    url: `/v1/core/video/runs/${encodeURIComponent(runId)}`
  })
  if (isApiError(response)) {
    return response
  }
  return response.data
}

export async function cancelVideoProcessingRun(runId: string): Promise<VideoProcessingRunRecord | ApiErrorResponse> {
  const response = await requestUrl<{ success: true; data: VideoProcessingRunRecord }>({
    method: 'POST',
    url: `/v1/core/video/runs/${encodeURIComponent(runId)}/cancel`
  })
  if (isApiError(response)) {
    return response
  }
  return response.data
}

export interface VideoProcessingSubscriptionCallbacks {
  onFrame: (frame: VideoProcessingStreamFrame) => void
  onClose: () => void
  onTransportError: (message: string) => void
}

export function subscribeVideoProcessingRun(
  runId: string,
  callbacks: VideoProcessingSubscriptionCallbacks
): () => void {
  const source = new EventSource(`/v1/core/video/runs/${encodeURIComponent(runId)}/stream`, { withCredentials: true })
  let closed = false

  const close = (): void => {
    if (closed) {
      return
    }
    closed = true
    try {
      source.close()
    } catch {
      /* ignore */
    }
    callbacks.onClose()
  }

  source.onmessage = (event: MessageEvent<string>) => {
    if (event.data === '[DONE]') {
      close()
      return
    }

    let parsed: unknown
    try {
      parsed = JSON.parse(event.data)
    } catch {
      callbacks.onTransportError('Malformed video-processing SSE frame.')
      return
    }

    if (!isVideoProcessingStreamFrame(parsed)) {
      callbacks.onTransportError('Unexpected video-processing SSE frame.')
      return
    }

    callbacks.onFrame(parsed)
  }

  source.onerror = () => {
    if (closed) {
      return
    }
    callbacks.onTransportError('Video-processing stream connection lost.')
    close()
  }

  return close
}

function isVideoProcessingStreamFrame(value: unknown): value is VideoProcessingStreamFrame {
  if (value == null || typeof value !== 'object') {
    return false
  }
  const frame = value as { type?: unknown }
  return (
    frame.type === 'snapshot' ||
    frame.type === 'status' ||
    frame.type === 'result' ||
    frame.type === 'error' ||
    frame.type === 'cancelled'
  )
}

// Models functions

export async function fetchModels() {
  return requestUrl<ModelsResponse>({
    method: 'GET',
    url: '/v1/models?includeAllAgents=true'
  })
}

// User data functions

export interface UpdateUserDataRequest {
  action: string
  secretName: string
  secretValue: string
}

export async function updateUserData(action: string, secretName: string, secretValue: string) {
  return requestUrl({
    method: 'POST',
    url: '/api/update-user-data',
    data: { action, secretName, secretValue }
  })
}

// Search MissionSquadCore Local Collection
export async function searchLocalCollection(
  collectionName: string, // This should be the safeName
  query: string,
  embeddingModelName: string,
  topK?: number // Add topK parameter
): Promise<{ success: boolean; results?: EmbeddingSearchResult[]; message?: string } | ApiErrorResponse> {
  const requestData: any = { query, embeddingModelName }
  if (topK !== undefined) {
    requestData.topK = topK
  }
  return requestUrl<{ success: boolean; results?: EmbeddingSearchResult[]; message?: string }>({
    method: 'POST',
    url: `/v1/core/collections/${collectionName}/search`,
    data: requestData
  })
}

// List MissionSquadCore Local Collections
export async function listLocalCollections(): Promise<{ success: boolean; data?: EmbeddingCollection[]; message?: string } | ApiErrorResponse> {
  return requestUrl<{ success: boolean; data?: EmbeddingCollection[]; message?: string }>({
    method: 'GET',
    url: '/v1/core/collections'
  })
}

export async function getScheduledRuns() {
  return requestUrl<ScheduledRun[]>({
    method: 'GET',
    url: '/v1/core/scheduled-runs'
  });
}

export async function createScheduledRun(data: NewScheduledRunPayload) {
  return requestUrl<ScheduledRun>({
    method: 'POST',
    url: '/v1/core/scheduled-runs',
    data
  });
}

export async function updateScheduledRun(id: string, data: UpdateScheduledRunPayload) {
  return requestUrl<ScheduledRun>({
    method: 'PUT',
    url: `/v1/core/scheduled-runs/${id}`,
    data
  });
}

export async function deleteScheduledRun(id: string) {
  return requestUrl<{ success: boolean; message: string }>({
    method: 'DELETE',
    url: `/v1/core/scheduled-runs/${id}`
  });
}

export async function toggleScheduledRun(id: string) {
  return requestUrl<ScheduledRun>({
    method: 'PUT',
    url: `/v1/core/scheduled-runs/${id}/toggle`
  });
}

export async function getScheduledRunResults(id: string, limit: number = 25, offset: number = 0) {
  return requestUrl<{ results: ScheduledRunResult[], total: number }>({
    method: 'GET',
    url: `/v1/core/scheduled-runs/${id}/results?limit=${limit}&offset=${offset}`
  });
}

/**
 * Admin Users API
 */

export async function adminListUsers(limit: number = 100, offset: number = 0) {
  return requestUrl<AdminListUsersResponse>({
    method: 'GET',
    url: `/v1/core/admin/users?limit=${limit}&offset=${offset}`
  });
}

export async function adminSetOrToggleCanPublish(username: string, value?: boolean) {
  const data = value !== undefined ? { value } : undefined;
  return requestUrl<AdminTogglePublishResponse>({
    method: 'PUT',
    url: `/v1/core/admin/users/${encodeURIComponent(username)}/can-publish`,
    data
  });
}

export async function adminDeleteUser(username: string) {
  return requestUrl<{ success: true }>({
    method: 'DELETE',
    url: `/v1/core/admin/users/${encodeURIComponent(username)}`
  });
}

export async function adminProvisionAccount(payload: AdminProvisionRequest) {
  return requestUrl<AdminProvisionResponse>({
    method: 'POST',
    url: '/v1/admin/provision',
    data: payload
  });
}

export async function adminGetUserProvisioningState(username: string) {
  return requestUrl<AdminProvisioningStateResponse>({
    method: 'GET',
    url: `/v1/core/admin/users/${encodeURIComponent(username)}/provisioning-state`
  });
}

/**
 * Usage Metrics (current user - session derived)
 */

// GET /v1/core/usage/summary
export async function getUsageSummary(params: {
  start?: string;
  end?: string;
  last?: string;
  modelName?: string;
  chatId?: string;
  tools?: boolean | 'true' | 'false';
}) {
  const qs = toQuery(params as any);
  return requestUrl<UsageSummaryResponse>({
    method: 'GET',
    url: `/v1/core/usage/summary${qs}`
  });
}

// GET /v1/core/usage/models
export async function getUsageByModel(params: {
  start?: string;
  end?: string;
  last?: string;
  chatId?: string;
  modelName?: string;
  tools?: boolean | 'true' | 'false';
}) {
  const qs = toQuery(params as any);
  return requestUrl<UsageByModelResponse>({
    method: 'GET',
    url: `/v1/core/usage/models${qs}`
  });
}

// GET /v1/core/usage/timeseries
export async function getUsageTimeseries(params: {
  start?: string;
  end?: string;
  last?: string;
  interval: 'hour' | 'day' | 'month';
  modelName?: string;
  chatId?: string;
  tools?: boolean | 'true' | 'false';
}) {
  const qs = toQuery(params as any);
  return requestUrl<UsageTimeseriesResponse>({
    method: 'GET',
    url: `/v1/core/usage/timeseries${qs}`
  });
}

// GET /v1/core/usage/billing
export async function getUsageBilling(params: {
  start?: string;
  end?: string;
  last?: string;
  groupBy: 'model' | 'model:day' | 'model:month';
  modelName?: string;
  chatId?: string;
  tools?: boolean | 'true' | 'false';
}) {
  const qs = toQuery(params as any);
  return requestUrl<BillingResponse>({
    method: 'GET',
    url: `/v1/core/usage/billing${qs}`
  });
}

// GET /v1/core/admin/usage/platform/billing
export async function adminGetPlatformUsageBilling(params: {
  start?: string;
  end?: string;
  last?: string;
  groupBy: 'model' | 'model:day' | 'model:month';
  modelName?: string;
  chatId?: string;
  tools?: boolean | 'true' | 'false';
}) {
  const qs = toQuery(params as any);
  return requestUrl<BillingResponse>({
    method: 'GET',
    url: '/v1/core/admin/usage/platform/billing' + qs
  });
}

// GET /v1/core/admin/usage/billing?username=...
export async function adminGetUserUsageBilling(params: {
  username: string;
  start?: string;
  end?: string;
  last?: string;
  groupBy: 'model' | 'model:day' | 'model:month';
  modelName?: string;
  chatId?: string;
  tools?: boolean | 'true' | 'false';
}) {
  const { username, ...rest } = params
  const qs = toQuery({ ...rest, username } as any);
  return requestUrl<BillingResponse>({
    method: 'GET',
    url: '/v1/core/admin/usage/billing' + qs
  });
}

// =====================
// Voices API Endpoints
// =====================

export async function listVoices() {
  return requestUrl<{ data: VoiceConfig[] }>({
    method: 'GET',
    url: '/v1/core/voices'
  })
}

export async function getVoice(name: string) {
  return requestUrl<{ data: VoiceConfig }>({
    method: 'GET',
    url: `/v1/core/voices/${encodeURIComponent(name)}`
  })
}

export async function addVoice(data: AddVoiceRequest) {
  return requestUrl<AddVoiceResponse>({
    method: 'POST',
    url: '/v1/core/add/voice',
    data
  })
}

export async function deleteVoice(name: string) {
  return requestUrl<{ status: string; message: string }>({
    method: 'POST',
    url: '/v1/core/delete/voice',
    data: { name }
  })
}

// Optional details (UI not required for initial flow)
export async function getVoiceDetails(data: { providerKey: ProviderKey; voiceId: string; apiKey?: string; url?: string }) {
  return requestUrl<{ data: any }>({
    method: 'POST',
    url: '/v1/core/voice/details',
    data
  })
}

export async function getSavedVoiceDetails(name: string, apiKey?: string, url?: string) {
  const payload: any = { name }
  if (apiKey) payload.apiKey = apiKey
  if (url) payload.url = url
  return requestUrl<{ data: any }>({
    method: 'POST',
    url: '/v1/core/voice/details/saved',
    data: payload
  })
}

/**
 * Non-streaming TTS synthesis.
 * Returns raw ArrayBuffer and response Content-Type for constructing a Blob on the FE.
 * Prefers saved voice alias path when 'voiceName' is provided; otherwise use raw provider+voice.
 */
export async function synthesizeSpeech(data: {
  text: string
  voiceName?: string
  providerKey?: ProviderKey
  voice?: string
  model?: string
  responseFormat?: 'mp3' | 'opus' | 'aac' | 'flac' | 'wav' | 'pcm'
  speed?: number
  ttsOptions?: Record<string, unknown>
  apiKey?: string
  url?: string
}): Promise<{ buffer: ArrayBuffer; contentType?: string } | ApiErrorResponse> {
  try {
    const response = await axios({
      method: 'POST',
      url: '/v1/core/audio/tts',
      data,
      responseType: 'arraybuffer'
    })
    return { buffer: response.data as ArrayBuffer, contentType: response.headers?.['content-type'] }
  } catch (error: any) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Failed to synthesize speech'
    const res: ApiErrorResponse = { error: true, message }
    return res
  }
}

/**
 * Server-side STT through the platform-wide speech-processing relay (admin-managed host + key —
 * the browser never sees the upstream API key). Auth is the signed-in user's normal token via
 * the axios interceptors; anonymous visitors get a 401.
 */
export async function transcribeSpeech(
  file: Blob,
  filename: string,
  opts?: { language?: string; prompt?: string }
): Promise<SpeechTranscriptionResponse | ApiErrorResponse> {
  const formData = new FormData()
  formData.append('file', file, filename)
  if (opts?.language) {
    formData.append('language', opts.language)
  }
  if (opts?.prompt) {
    formData.append('prompt', opts.prompt)
  }

  return requestUrl<SpeechTranscriptionResponse>({
    method: 'POST',
    url: '/v1/core/speech-processing/transcribe',
    data: formData,
    file: true,
    timeout: 60000
  })
}

// =====================
// MCP Server Install Requests
// =====================

export async function requestMcpServerInstall(payload: RequestMcpServerInstallPayload) {
  return requestUrl<RequestMcpServerInstallResponse>({
    method: 'POST',
    url: '/v1/mcp/server-requests',
    data: payload
  })
}

// =====================
// Admin MCP Server Request Management
// =====================

export async function adminListMcpServerRequests(params?: {
  status?: 'pending' | 'approved' | 'rejected' | 'installed'
  requestType?: 'platform_install' | 'external_removal'
  q?: string
  limit?: number
  offset?: number
}) {
  const qs = toQuery({
    status: params?.status,
    requestType: params?.requestType,
    q: params?.q,
    limit: params?.limit,
    offset: params?.offset
  })

  return requestUrl<AdminListMcpServerRequestsResponse>({
    method: 'GET',
    url: `/v1/mcp/admin/server-requests${qs}`
  })
}

export async function adminUpdateMcpServerRequest(
  id: string,
  payload: AdminUpdateMcpServerRequestPayload
) {
  return requestUrl<AdminUpdateMcpServerRequestResponse>({
    method: 'PUT',
    url: `/v1/mcp/admin/server-requests/${encodeURIComponent(id)}`,
    data: payload
  })
}

// =====================
// Agent Pages API (contracts §11 — authenticated builder routes)
// =====================

export async function listPages() {
  return requestUrl<PageListResponse>({
    method: 'GET',
    url: '/v1/core/pages'
  })
}

export async function getPage(id: string) {
  return requestUrl<PageResponse>({
    method: 'GET',
    url: `/v1/core/pages/${encodeURIComponent(id)}`
  })
}

export async function createPage(input: PageDraftInput) {
  return requestUrl<PageResponse>({
    method: 'POST',
    url: '/v1/core/pages',
    data: input
  })
}

export async function updatePage(id: string, input: PageDraftInput) {
  return requestUrl<PageResponse>({
    method: 'PUT',
    url: `/v1/core/pages/${encodeURIComponent(id)}`,
    data: input
  })
}

export async function deletePage(id: string) {
  return requestUrl<PageDeleteResponse>({
    method: 'DELETE',
    url: `/v1/core/pages/${encodeURIComponent(id)}`
  })
}

export async function publishPage(id: string) {
  return requestUrl<PageResponse>({
    method: 'POST',
    url: `/v1/core/pages/${encodeURIComponent(id)}/publish`
  })
}

export async function unpublishPage(id: string) {
  return requestUrl<PageResponse>({
    method: 'POST',
    url: `/v1/core/pages/${encodeURIComponent(id)}/unpublish`
  })
}

export async function previewRunPage(id: string, input?: Record<string, string>) {
  return requestUrl<PagePreviewRunResponse>({
    method: 'POST',
    url: `/v1/core/pages/${encodeURIComponent(id)}/preview-run`,
    data: input ? { input } : {}
  })
}

export async function getPageRuns(id: string, limit: number = 25, offset: number = 0) {
  return requestUrl<PageRunsResponse>({
    method: 'GET',
    url: `/v1/core/pages/${encodeURIComponent(id)}/runs?limit=${limit}&offset=${offset}`
  })
}

/** Owner history cleanup — also removes the run from public history/permalinks. 409 while active. */
export async function deletePageRun(id: string, runId: string) {
  return requestUrl<PageDeleteResponse>({
    method: 'DELETE',
    url: `/v1/core/pages/${encodeURIComponent(id)}/runs/${encodeURIComponent(runId)}`
  })
}

export async function getPagePreviewToken(id: string) {
  return requestUrl<PagePreviewTokenResponse>({
    method: 'GET',
    url: `/v1/core/pages/${encodeURIComponent(id)}/preview-token`
  })
}

export async function getX402Networks() {
  return requestUrl<X402NetworksResponse>({
    method: 'GET',
    url: '/v1/core/x402/networks'
  })
}

/** §26 auto-expanding category suggestions (seeds ∪ categories in use across all pages). */
export async function getPageCategories() {
  return requestUrl<PageCategoriesResponse>({
    method: 'GET',
    url: '/v1/core/pages/categories'
  })
}

/**
 * The owner's x402 settlement ledger (paid agents AND paid pages). Page rows
 * carry the pageId inside `resourceId` — filter client-side with
 * paidPageIdFromResourceId (the endpoint has no pageId param).
 */
export async function getX402Settlements(params?: { start?: number; end?: number }) {
  const query = new URLSearchParams()
  if (params?.start !== undefined) query.set('start', String(params.start))
  if (params?.end !== undefined) query.set('end', String(params.end))
  const suffix = query.toString()
  return requestUrl<X402SettlementsResponse>({
    method: 'GET',
    url: `/v1/core/x402/settlements${suffix ? `?${suffix}` : ''}`
  })
}

/**
 * Compiles a (possibly unsaved) page layout into the JSON Schema its runs
 * enforce, so the owner can copy it into the source agent's prompt.
 */
export async function compilePageLayoutSchema(layout: PageLayout) {
  return requestUrl<PageLayoutSchemaResponse>({
    method: 'POST',
    url: '/v1/core/pages/layout-schema',
    data: { layout }
  })
}
